import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Spinner,
} from "reactstrap";
import { authenticatedFetch } from "../utils/apiUtils";

const ChangeEncryptionPasswordModal = ({
  show,
  onClose,
  onSuccess,
  startInRecoveryMode = false,
}) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recoveryKey, setRecoveryKey] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [confirmDataLoss, setConfirmDataLoss] = useState(false);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (show) {
      checkChangeImpact();
      // Reset form state
      setShowPasswordForm(!startInRecoveryMode);
      setShowRecoveryForm(false); // Will be set after security level is determined
      setShowResetForm(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setRecoveryKey("");
      setAccountPassword("");
      setConfirmDataLoss(false);
      setError("");
    }
  }, [show, startInRecoveryMode]);

  // Set the correct form based on security level and recovery mode
  useEffect(() => {
    if (show && impact && startInRecoveryMode) {
      if (impact.securityLevel === "Enhanced") {
        // Enhanced security can use recovery keys
        setShowRecoveryForm(true);
        setShowPasswordForm(false);
      } else if (impact.securityLevel === "Maximum") {
        // Maximum security has no recovery - show different options
        setShowRecoveryForm(false);
        setShowPasswordForm(false); // Don't auto-show password form, let user choose
      } else {
        // Standard security
        setShowRecoveryForm(false);
        setShowPasswordForm(true);
      }
    }
  }, [show, impact, startInRecoveryMode]);

  const checkChangeImpact = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(
        "/change-encryption-password/check-change-impact",
        {
          method: "POST",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setImpact(data);
      } else {
        setError("Failed to check encryption password change options");
      }
    } catch (error) {
      console.error("Error checking change options:", error);
      setError(
        "Error checking change options: " + (error?.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNormalChange = () => {
    setShowPasswordForm(true);
  };

  const handleRecoveryChange = () => {
    setShowRecoveryForm(true);
  };

  const handleDataReset = () => {
    setShowResetForm(true);
  };

  const submitPasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (!currentPassword.trim()) {
      setError("Current password is required");
      return;
    }

    if (!newPassword.trim()) {
      setError("New password is required");
      return;
    }

    setProcessing(true);
    setError("");

    try {
      const response = await authenticatedFetch(
        "/change-encryption-password/change-password",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            currentPassword,
            newPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        console.log("[DEBUG] Normal password change success response:", data);
        
        // Always show the enhanced modal for better UX
        const modalDiv = document.createElement('div');
        modalDiv.style.cssText = `
          position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
          background: rgba(0,0,0,0.8); z-index: 10000; display: flex; 
          align-items: center; justify-content: center;
        `;
        
        const contentDiv = document.createElement('div');
        contentDiv.style.cssText = `
          background: white; padding: 20px; border-radius: 8px; max-width: 600px; 
          max-height: 80vh; overflow-y: auto; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        `;
        
        contentDiv.innerHTML = `
          <h3 style="color: #28a745; margin-top: 0;">✅ Password Changed Successfully!</h3>
          <p><strong>${data.message}</strong></p>
          ${data.newRecoveryKey ? `
            <div style="background: #f8f9fa; border: 2px solid #28a745; border-radius: 4px; padding: 15px; margin: 15px 0;">
              <h4 style="color: #dc3545; margin-top: 0;">🔑 NEW RECOVERY KEY</h4>
              <p style="color: #dc3545; font-weight: bold;">SAVE THIS SECURELY - Your previous recovery key will no longer work!</p>
              <textarea id="recoveryKeyText" style="width: 100%; height: 120px; font-family: monospace; font-size: 12px; border: 1px solid #ccc; padding: 8px; border-radius: 4px;" readonly>${data.newRecoveryKey}</textarea>
              <div style="margin-top: 10px;">
                <button id="copyKeyBtn" style="background: #007bff; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin-right: 10px;">📋 Copy Recovery Key</button>
                <span id="copyStatus" style="color: #28a745; font-weight: bold;"></span>
              </div>
            </div>
          ` : ''}
          <div style="text-align: right; margin-top: 20px;">
            <button id="closeModalBtn" style="background: #6c757d; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer;">Close</button>
          </div>
        `;
        
        modalDiv.appendChild(contentDiv);
        document.body.appendChild(modalDiv);
        
        // Add event listeners
        const copyBtn = document.getElementById('copyKeyBtn');
        const copyStatus = document.getElementById('copyStatus');
        const closeBtn = document.getElementById('closeModalBtn');
        const textarea = document.getElementById('recoveryKeyText');
        
        if (copyBtn && textarea) {
          copyBtn.addEventListener('click', () => {
            textarea.select();
            textarea.setSelectionRange(0, 99999); // For mobile devices
            navigator.clipboard.writeText(textarea.value).then(() => {
              copyStatus.textContent = '✅ Copied to clipboard!';
              setTimeout(() => copyStatus.textContent = '', 3000);
            }).catch(() => {
              copyStatus.textContent = '⚠️ Please manually copy the text above';
            });
          });
        }
        
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            document.body.removeChild(modalDiv);
          });
        }
        
        // Close on backdrop click
        modalDiv.addEventListener('click', (e) => {
          if (e.target === modalDiv) {
            document.body.removeChild(modalDiv);
          }
        });
        
        onClose();
      } else {
        setError(data.message || "Failed to change encryption password");
      }
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error && typeof error.message === "string"
          ? error.message
          : "Unknown error occurred";
      setError("Failed to change encryption password: " + errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const submitRecoveryChange = async () => {
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (!recoveryKey.trim()) {
      setError("Recovery key is required");
      return;
    }

    if (!newPassword.trim()) {
      setError("New password is required");
      return;
    }

    setProcessing(true);
    setError("");

    try {
      const response = await authenticatedFetch(
        "/change-encryption-password/change-with-recovery",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            recoveryKey,
            newPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        let successMessage = data.message;

        // If a new recovery key was generated, add it to the message
        if (data.newRecoveryKey) {
          successMessage += `\n\n🔑 NEW RECOVERY KEY (SAVE THIS SECURELY):\n${data.newRecoveryKey}\n\n⚠️ Your previous recovery key will no longer work. Please save this new recovery key in a secure location and delete your old one.`;
        } else {
          console.log("[DEBUG] No newRecoveryKey in response:", data);
        }

        console.log("[DEBUG] Recovery success response:", data);
        console.log("[DEBUG] onSuccess type:", typeof onSuccess);
        
        if (typeof onSuccess === 'function') {
          onSuccess(successMessage);
        } else {
          console.error("onSuccess is not a function:", onSuccess);
          // Better fallback - create a copyable text area
          const recoveryKeySection = data.newRecoveryKey ? 
            `\n\n🔑 NEW RECOVERY KEY (SAVE THIS SECURELY):\n${data.newRecoveryKey}\n\n⚠️ Your previous recovery key will no longer work. Please save this new recovery key in a secure location and delete your old one.` : '';
          
          // Create a temporary modal-like div with copyable content
          const modalDiv = document.createElement('div');
          modalDiv.style.cssText = `
            position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
            background: rgba(0,0,0,0.8); z-index: 10000; display: flex; 
            align-items: center; justify-content: center;
          `;
          
          const contentDiv = document.createElement('div');
          contentDiv.style.cssText = `
            background: white; padding: 20px; border-radius: 8px; max-width: 600px; 
            max-height: 80vh; overflow-y: auto; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          `;
          
          contentDiv.innerHTML = `
            <h3 style="color: #28a745; margin-top: 0;">✅ Password Changed Successfully!</h3>
            <p><strong>${data.message}</strong></p>
            ${data.newRecoveryKey ? `
              <div style="background: #f8f9fa; border: 2px solid #28a745; border-radius: 4px; padding: 15px; margin: 15px 0;">
                <h4 style="color: #dc3545; margin-top: 0;">🔑 NEW RECOVERY KEY</h4>
                <p style="color: #dc3545; font-weight: bold;">SAVE THIS SECURELY - Your previous recovery key will no longer work!</p>
                <textarea id="recoveryKeyText" style="width: 100%; height: 120px; font-family: monospace; font-size: 12px; border: 1px solid #ccc; padding: 8px; border-radius: 4px;" readonly>${data.newRecoveryKey}</textarea>
                <div style="margin-top: 10px;">
                  <button id="copyKeyBtn" style="background: #007bff; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin-right: 10px;">📋 Copy Recovery Key</button>
                  <span id="copyStatus" style="color: #28a745; font-weight: bold;"></span>
                </div>
              </div>
            ` : ''}
            <div style="text-align: right; margin-top: 20px;">
              <button id="closeModalBtn" style="background: #6c757d; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer;">Close</button>
            </div>
          `;
          
          modalDiv.appendChild(contentDiv);
          document.body.appendChild(modalDiv);
          
          // Add event listeners
          const copyBtn = document.getElementById('copyKeyBtn');
          const copyStatus = document.getElementById('copyStatus');
          const closeBtn = document.getElementById('closeModalBtn');
          const textarea = document.getElementById('recoveryKeyText');
          
          if (copyBtn && textarea) {
            copyBtn.addEventListener('click', () => {
              textarea.select();
              textarea.setSelectionRange(0, 99999); // For mobile devices
              navigator.clipboard.writeText(textarea.value).then(() => {
                copyStatus.textContent = '✅ Copied to clipboard!';
                setTimeout(() => copyStatus.textContent = '', 3000);
              }).catch(() => {
                copyStatus.textContent = '⚠️ Please manually copy the text above';
              });
            });
          }
          
          if (closeBtn) {
            closeBtn.addEventListener('click', () => {
              document.body.removeChild(modalDiv);
            });
          }
          
          // Close on backdrop click
          modalDiv.addEventListener('click', (e) => {
            if (e.target === modalDiv) {
              document.body.removeChild(modalDiv);
            }
          });
        }
        onClose();
      } else {
        setError(
          data.message ||
            "Failed to change encryption password with recovery key"
        );
      }
    } catch (error) {
      console.error("Recovery password change error:", error);
      const errorMessage =
        typeof error === "string"
          ? error
          : error && typeof error.message === "string"
          ? error.message
          : "Unknown error occurred";
      setError("Failed to change encryption password: " + errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const submitDataReset = async () => {
    if (!accountPassword.trim()) {
      setError("Account password is required to verify your identity");
      return;
    }

    if (!confirmDataLoss) {
      setError(
        "You must confirm that you understand all encrypted data will be deleted"
      );
      return;
    }

    setProcessing(true);
    setError("");

    try {
      const response = await authenticatedFetch(
        "/api/DataReset/reset-encrypted-data",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            currentPassword: accountPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        onSuccess?.(
          data.message ||
            "Encryption password reset successful! All encrypted data has been deleted and you've been reset to Standard security."
        );
        onClose();
        // Refresh page to update security level display
        window.location.reload();
      } else {
        setError(data.message || "Failed to reset encryption password");
      }
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error && typeof error.message === "string"
          ? error.message
          : "Unknown error occurred";
      setError("Failed to reset encryption password: " + errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <Spinner color="primary" />
          <p className="mt-2">Checking encryption password options...</p>
        </div>
      );
    }

    if (!impact) {
      return (
        <Alert color="danger">
          Failed to load encryption password options.
        </Alert>
      );
    }

    // Standard security - can't change encryption password
    if (!impact.impact.canChange) {
      return (
        <div>
          <Alert color="info">
            <h6>Standard Security Level</h6>
            <p className="mb-0">{impact.impact.message}</p>
            <p className="mb-0 mt-2">
              <strong>{impact.impact.warning}</strong>
            </p>
          </Alert>
        </div>
      );
    }

    // Enhanced/Maximum - show password change options
    if (showPasswordForm) {
      return (
        <Form>
          <FormGroup>
            <Label for="currentPassword">Current Encryption Password</Label>
            <Input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current encryption password"
            />
          </FormGroup>
          <FormGroup>
            <Label for="newPassword">New Encryption Password</Label>
            <Input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter a new encryption password"
            />
          </FormGroup>
          <FormGroup>
            <Label for="confirmPassword">Confirm New Password</Label>
            <Input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new encryption password"
            />
          </FormGroup>
        </Form>
      );
    }

    if (showRecoveryForm) {
      return (
        <Form>
          <Alert color="warning">
            <strong>Recovery Key Required</strong>
            <p className="mb-0 mt-1">
              Use your recovery key if you've forgotten your current encryption
              password.
            </p>
          </Alert>
          <FormGroup>
            <Label for="recoveryKey">Recovery Key</Label>
            <Input
              type="text"
              id="recoveryKey"
              value={recoveryKey}
              onChange={(e) => setRecoveryKey(e.target.value)}
              placeholder="Enter your 24-word recovery key"
            />
          </FormGroup>
          <FormGroup>
            <Label for="newPassword">New Encryption Password</Label>
            <Input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter a new encryption password"
            />
          </FormGroup>
          <FormGroup>
            <Label for="confirmPassword">Confirm New Password</Label>
            <Input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new encryption password"
            />
          </FormGroup>
        </Form>
      );
    }

    if (showResetForm) {
      return (
        <Form>
          <Alert color="danger">
            <h5>⚠️ CRITICAL WARNING ⚠️</h5>
            <p>
              <strong>
                This will permanently delete ALL your encrypted data!
              </strong>
            </p>
            <p>
              Since you've forgotten your encryption password and Maximum
              security has no recovery options, the only way to reset is to
              delete all encrypted data.
            </p>
            <p className="mb-0">
              After reset, you'll be downgraded to Standard security and can
              upgrade again.
            </p>
          </Alert>
          <FormGroup>
            <Label for="accountPassword">
              Your Account Password{" "}
              <small className="text-muted">(to verify your identity)</small>
            </Label>
            <Input
              type="password"
              id="accountPassword"
              value={accountPassword}
              onChange={(e) => setAccountPassword(e.target.value)}
              placeholder="Enter your account login password"
              required
            />
            <FormText color="muted">
              This is your login password, not your encryption password.
            </FormText>
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                checked={confirmDataLoss}
                onChange={(e) => setConfirmDataLoss(e.target.checked)}
              />
              I understand that ALL my encrypted data will be permanently
              deleted and cannot be recovered
            </Label>
          </FormGroup>
        </Form>
      );
    }

    // Initial options
    return (
      <div>
        <Alert color="info">
          <h6>{impact.securityLevel} Security Level</h6>
          <p className="mb-0">{impact.impact.message}</p>
          <p className="mb-0 mt-2">
            <strong>{impact.impact.warning}</strong>
          </p>
        </Alert>

        <div className="d-grid gap-2">
          <Button color="primary" onClick={handleNormalChange}>
            <i className="fas fa-key me-2"></i>
            Change with Current Password
          </Button>
          {impact.securityLevel === "Enhanced" && (
            <Button color="outline-secondary" onClick={handleRecoveryChange}>
              <i className="fas fa-life-ring me-2"></i>
              Change with Recovery Key
            </Button>
          )}
          {impact.securityLevel === "Maximum" && startInRecoveryMode && (
            <Button color="danger" onClick={handleDataReset}>
              <i className="fas fa-trash me-2"></i>
              Reset (Delete All Data)
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    if (loading || !impact?.impact.canChange) {
      return (
        <Button color="secondary" onClick={onClose}>
          Close
        </Button>
      );
    }

    if (showPasswordForm) {
      return (
        <>
          <Button color="secondary" onClick={() => setShowPasswordForm(false)}>
            Back
          </Button>
          <Button
            color="primary"
            onClick={submitPasswordChange}
            disabled={processing}
          >
            {processing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Changing...
              </>
            ) : (
              "Change Password"
            )}
          </Button>
        </>
      );
    }

    if (showRecoveryForm) {
      return (
        <>
          <Button color="secondary" onClick={() => setShowRecoveryForm(false)}>
            Back
          </Button>
          <Button
            color="primary"
            onClick={submitRecoveryChange}
            disabled={processing}
          >
            {processing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Changing...
              </>
            ) : (
              "Change Password"
            )}
          </Button>
        </>
      );
    }

    if (showResetForm) {
      return (
        <>
          <Button color="secondary" onClick={() => setShowResetForm(false)}>
            Back
          </Button>
          <Button
            color="danger"
            onClick={submitDataReset}
            disabled={processing || !accountPassword || !confirmDataLoss}
          >
            {processing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Resetting...
              </>
            ) : (
              "Reset Encryption Password & Delete All Data"
            )}
          </Button>
        </>
      );
    }

    return (
      <Button color="secondary" onClick={onClose}>
        Cancel
      </Button>
    );
  };

  return (
    <Modal isOpen={show} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Change Encryption Password</ModalHeader>
      <ModalBody>
        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {renderContent()}
      </ModalBody>
      <ModalFooter>{renderFooter()}</ModalFooter>
    </Modal>
  );
};

export default ChangeEncryptionPasswordModal;
